import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "../lib/apiClient";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import { SlaCountdown } from "../components/SlaCountdown";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, User, Phone, MapPin, Wrench, Calendar, Camera, History,
} from "lucide-react";

export default function ComplaintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [meta, setMeta] = useState({ statuses: [] });
  const [engineers, setEngineers] = useState([]);
  const [note, setNote] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [schedule, setSchedule] = useState("");

  const isStaff = user?.role === "admin" || user?.role === "frontdesk";

  const load = useCallback(() => {
    api.get(`/complaints/${id}`).then((r) => { setC(r.data); setNewStatus(r.data.status); setSchedule(r.data.scheduled_visit_date || ""); });
  }, [id]);

  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data));
    if (isStaff) api.get("/engineers").then((r) => setEngineers(r.data));
  }, [load, isStaff]);

  if (!c) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const assign = async (engineer_id) => {
    try { await api.post(`/complaints/${id}/assign`, { engineer_id }); toast.success("Engineer assigned."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeStatus = async () => {
    try { await api.patch(`/complaints/${id}/status`, { status: newStatus, note: statusNote }); toast.success(`Status: ${newStatus}`); setStatusNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note) return;
    try { await api.post(`/complaints/${id}/notes`, { note }); toast.success("Note added."); setNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveSchedule = async () => {
    if (!schedule) return;
    try { await api.patch(`/complaints/${id}/schedule`, { scheduled_visit_date: schedule }); toast.success("Visit scheduled."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/complaints/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo uploaded."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 sm:p-8 fade-up">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-klein mb-4" data-testid="back-button">
        <ArrowLeft size={15} /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-klein font-bold text-lg" data-testid="detail-complaint-no">{c.complaint_no}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">{c.issue_category}</h1>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={c.status} />
            <PriorityBadge priority={c.priority} />
            {c.flags?.recurring && <span className="text-xs font-bold bg-signal text-white px-2 py-0.5">RECURRING</span>}
            {c.flags?.repeat && !c.flags?.recurring && <span className="text-xs font-bold bg-warn px-2 py-0.5">REPEAT</span>}
          </div>
        </div>
      </div>

      {/* SLA countdowns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 max-w-2xl">
        <SlaCountdown label="Time left to assign" secondsLeft={c.sla?.assign_seconds_left} testId="sla-assign" />
        <SlaCountdown label="Time left to attend" secondsLeft={c.sla?.attend_seconds_left} paused={c.sla?.attend_paused} testId="sla-attend" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: info + actions */}
        <div className="lg:col-span-2 space-y-5">
          {/* Customer + appliance */}
          <div className="border border-border bg-white p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="label-caps text-muted-foreground mb-2">Customer</div>
              <div className="flex items-center gap-2 text-sm"><User size={14} /> {c.customer?.name}</div>
              <div className="flex items-center gap-2 text-sm mt-1"><Phone size={14} /> {c.customer?.mobile}</div>
              <div className="flex items-start gap-2 text-sm mt-1"><MapPin size={14} className="mt-0.5" /> {[c.customer?.address, c.customer?.city, c.customer?.pincode].filter(Boolean).join(", ") || "—"}</div>
            </div>
            <div>
              <div className="label-caps text-muted-foreground mb-2">Appliance</div>
              <div className="text-sm"><Wrench size={14} className="inline mr-2" />{c.appliance?.product_type}</div>
              <div className="text-sm mt-1 text-muted-foreground">{c.appliance?.brand} {c.appliance?.model_number}</div>
              <div className="text-sm mt-1">Bill: {c.appliance?.bill_number || "—"} · {c.appliance?.warranty_status}</div>
            </div>
            {c.description && <div className="sm:col-span-2 text-sm border-t border-border pt-3 text-muted-foreground">{c.description}</div>}
          </div>

          {/* Actions */}
          <div className="border border-border bg-white p-5 space-y-4">
            <div className="label-caps text-klein">Actions</div>

            {isStaff && (
              <div>
                <label className="text-sm font-medium">Assign / reassign engineer</label>
                <Select value={c.assigned_engineer_id || ""} onValueChange={assign}>
                  <SelectTrigger data-testid="assign-engineer-select" className="mt-1 rounded-sm"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                  <SelectContent>{engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.zone}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Change status</label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Optional note (e.g. resolution)" value={statusNote} data-testid="status-note" onChange={(e) => setStatusNote(e.target.value)} className="rounded-sm" />
                <Button onClick={changeStatus} data-testid="update-status-button" className="rounded-sm bg-ink hover:bg-ink/90 text-white whitespace-nowrap">Update</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <button onClick={() => { setNewStatus("In Progress"); }} className="text-xs border border-border px-2 py-1 hover:bg-secondary" data-testid="quick-inprogress">Mark Attended (In Progress)</button>
                <button onClick={() => { setNewStatus("Resolved"); }} className="text-xs border border-border px-2 py-1 hover:bg-secondary">Resolve</button>
                <button onClick={() => { setNewStatus("Reopened"); }} className="text-xs border border-border px-2 py-1 hover:bg-secondary">Reopen</button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium flex items-center gap-1"><Calendar size={14} /> Schedule visit</label>
              <div className="flex gap-2 mt-1">
                <Input type="date" value={schedule?.slice(0, 10)} data-testid="schedule-input" onChange={(e) => setSchedule(e.target.value)} className="rounded-sm" />
                <Button onClick={saveSchedule} variant="outline" className="rounded-sm" data-testid="save-schedule">Save</Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Add note</label>
              <div className="flex gap-2 mt-1">
                <Textarea value={note} data-testid="note-input" onChange={(e) => setNote(e.target.value)} className="rounded-sm" rows={2} />
                <Button onClick={addNote} variant="outline" className="rounded-sm self-end" data-testid="add-note-button">Add</Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium flex items-center gap-1"><Camera size={14} /> Photos</label>
              <input type="file" accept="image/*" capture="environment" data-testid="photo-input" className="mt-1 text-sm block" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              {c.photos?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {c.photos.map((p) => (
                    <img key={p.id} src={`${API}/files/${p.storage_path}`} alt="complaint" className="h-20 w-20 object-cover border border-border" />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: activity */}
        <div className="border border-border bg-white p-5">
          <div className="label-caps text-klein flex items-center gap-2 mb-4"><History size={14} /> Activity log</div>
          <div className="space-y-4">
            {c.activity?.map((a, i) => (
              <div key={i} className="relative pl-4 border-l-2 border-border" data-testid="activity-item">
                <div className="absolute -left-[5px] top-1 h-2 w-2 bg-klein rounded-full" />
                <div className="text-sm font-semibold">{a.action}</div>
                {a.details && <div className="text-xs text-muted-foreground mt-0.5">{a.details}</div>}
                <div className="text-[11px] text-muted-foreground mt-1 font-mono">{a.actor} · {new Date(a.timestamp).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
